import { useCallback, useEffect, useRef, useState } from 'react';
import { showToast } from '@/components/Toast';
import { useRegisterSW } from '@/pwaRegistration';

/**
 * Non-blocking, dismissible "new version available" banner for the PWA.
 *
 * `useRegisterSW` owns the prompt-mode lifecycle. Clicking Refresh sends the
 * generated worker's SKIP_WAITING message; clientsClaim then makes that worker
 * control this page, and the one-shot onNeedReload callback reloads the new
 * precached app shell. We never auto-reload a field device mid-session.
 */

// One delayed nudge keeps an obsolete app visible without interrupting field
// work every ten minutes. A different waiting worker starts a new cycle.
export const UPDATE_REMINDER_DELAY_MS = 60 * 60 * 1000;
export const MAX_REMINDERS_PER_WORKER = 1;

/** Guard controller changes so one worker activation can cause only one reload. */
export function createReloadOnce(
  reload: () => void = () => window.location.reload(),
): () => void {
  let didReload = false;
  return () => {
    if (didReload) return;
    didReload = true;
    reload();
  };
}

export const REFRESH_RETRY_INTERVAL_MS = 1500;
export const REFRESH_MAX_ATTEMPTS = 4;

/**
 * A cold waiting worker evaluates its script asynchronously (AMD-wrapped
 * generateSW output), so the first SKIP_WAITING message can land before its
 * listener exists and be lost — observed live on 2026-07-16 (#685): activation
 * only happened after repeated messages. Retry a few times; if the worker
 * still hasn't taken control, leave the page stable and report the failure so
 * the user can close another open tab and retry without entering a reload loop.
 */
export function createRefreshWithRetry(deps: {
  requestUpdate: () => void | Promise<void>;
  isDone: () => boolean;
  onFailure: () => void;
  intervalMs?: number;
  maxAttempts?: number;
}): () => void {
  const {
    requestUpdate,
    isDone,
    onFailure,
    intervalMs = REFRESH_RETRY_INTERVAL_MS,
    maxAttempts = REFRESH_MAX_ATTEMPTS,
  } = deps;
  let started = false;
  return () => {
    if (started) return;
    started = true;
    let attempts = 0;
    const attempt = () => {
      if (isDone()) return; // controllerchange already reloaded us
      if (attempts >= maxAttempts) {
        started = false;
        onFailure();
        return;
      }
      attempts += 1;
      try {
        void Promise.resolve(requestUpdate()).catch(() => undefined);
      } catch {
        // A later retry or the final failure state handles sync failures too.
      }
      setTimeout(attempt, intervalMs);
    };
    attempt();
  };
}

type WaitingRegistration = Pick<ServiceWorkerRegistration, 'waiting'>;

export function isWorkerStillWaiting(
  registration: WaitingRegistration | null | undefined,
  worker: ServiceWorker | null | undefined,
): boolean {
  return Boolean(worker && worker.state === 'installed' && registration?.waiting === worker);
}

export function PwaRefreshPrompt() {
  const reminderTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const registration = useRef<ServiceWorkerRegistration | null>(null);
  const promptedWorker = useRef<ServiceWorker | null>(null);
  const reloadTriggered = useRef(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);
  const reloadOnce = useRef<(() => void) | null>(null);
  if (reloadOnce.current === null) {
    const once = createReloadOnce();
    reloadOnce.current = () => {
      reloadTriggered.current = true;
      once();
    };
  }

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onNeedReload: reloadOnce.current,
    onOfflineReady() {
      showToast('Ready to work offline', 'info');
    },
    onRegisteredSW(_swScriptUrl, registeredWorker) {
      registration.current = registeredWorker ?? null;
    },
  });

  const refreshWithRetry = useRef<(() => void) | null>(null);
  if (refreshWithRetry.current === null) {
    refreshWithRetry.current = createRefreshWithRetry({
      requestUpdate: () => updateServiceWorker(true),
      isDone: () => reloadTriggered.current,
      onFailure: () => {
        setRefreshFailed(true);
      },
    });
  }

  // A newly detected waiting worker supersedes the previous worker's snooze.
  useEffect(() => {
    if (!needRefresh) return;
    const waitingWorker = registration.current?.waiting ?? null;
    if (waitingWorker && waitingWorker !== promptedWorker.current) {
      promptedWorker.current = waitingWorker;
      setReminderCount(0);
    }
    setRefreshFailed(false);
    if (reminderTimer.current !== null) {
      clearTimeout(reminderTimer.current);
      reminderTimer.current = null;
    }
  }, [needRefresh]);

  useEffect(() => {
    return () => {
      if (reminderTimer.current !== null) {
        clearTimeout(reminderTimer.current);
      }
    };
  }, []);

  const remindLater = useCallback(() => {
    if (!needRefresh) return;
    const waitingWorker = registration.current?.waiting ?? null;
    setNeedRefresh(false);
    if (reminderTimer.current !== null) {
      clearTimeout(reminderTimer.current);
    }
    if (!isWorkerStillWaiting(registration.current, waitingWorker)) return;
    let workerReminderCount = reminderCount;
    if (waitingWorker !== promptedWorker.current) {
      promptedWorker.current = waitingWorker;
      workerReminderCount = 0;
      setReminderCount(0);
    }
    if (workerReminderCount >= MAX_REMINDERS_PER_WORKER) return;
    setReminderCount(workerReminderCount + 1);
    reminderTimer.current = setTimeout(() => {
      reminderTimer.current = null;
      if (isWorkerStillWaiting(registration.current, waitingWorker)) {
        setNeedRefresh(true);
      }
    }, UPDATE_REMINDER_DELAY_MS);
  }, [needRefresh, reminderCount, setNeedRefresh]);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(var(--space-4, 16px) + env(safe-area-inset-bottom, 0px))',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3, 12px)',
        maxWidth: 'calc(100vw - 32px)',
        padding: '10px 14px',
        borderRadius: 'var(--ft-r-md, 10px)',
        background: 'var(--ft-ink-900, #1a1f26)',
        color: '#fff',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
        fontSize: 'var(--text-sm, 0.875rem)',
      }}
    >
      <span>
        {refreshFailed
          ? 'The update is still waiting. Close other app tabs and retry.'
          : 'A new version is available.'}
      </span>
      <button
        type="button"
        onClick={() => {
          setRefreshFailed(false);
          refreshWithRetry.current?.();
        }}
        style={{
          border: 'none',
          borderRadius: 'var(--ft-r-sm, 6px)',
          padding: '6px 12px',
          background: 'var(--ft-primary, #026aa7)',
          color: '#fff',
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {refreshFailed ? 'Retry' : 'Refresh'}
      </button>
      <button
        type="button"
        aria-label={reminderCount < MAX_REMINDERS_PER_WORKER
          ? 'Remind me about this update later'
          : 'Dismiss this update for this session'}
        title={reminderCount < MAX_REMINDERS_PER_WORKER
          ? 'Remind me in 1 hour'
          : 'Dismiss for this session'}
        onClick={remindLater}
        style={{
          border: 'none',
          background: 'transparent',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '1.1rem',
          lineHeight: 1,
          cursor: 'pointer',
          padding: '4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
